import { soundAtom } from '@openeducation/oe-rn-jotai-atoms';
import {
  Audio,
  AVPlaybackStatus,
  AVPlaybackStatusError,
  AVPlaybackStatusSuccess,
} from 'expo-av';
import { useAtom } from 'jotai';
import React from 'react';
import { Platform } from 'react-native';

import { saveAudioToLocalCache } from '../helpers/save-audio-to-local-cache';

const PLAY_SOUND_DELAY_MS = 25;

export type AudioBehaviorMode =
  | 'STOP_OTHERS_AND_BE_STOPPED'
  | 'STOP_OTHERS_ONLY'
  | 'BE_STOPPED_ONLY'
  | 'NONE';

export interface UseAudioParams {
  audioUrl?: string | null;
  autoPlay?: boolean;
  updateIntervalMs?: number;
  audioBehaviorMode?: AudioBehaviorMode;
  saveBase64AudioToCache?: boolean;
  cacheFolder?: string;
}

export function useAudio({
  audioUrl,
  autoPlay = false,
  updateIntervalMs = 1000,
  audioBehaviorMode = 'STOP_OTHERS_AND_BE_STOPPED',
  saveBase64AudioToCache = true,
  cacheFolder = 'audio',
}: UseAudioParams) {
  const soundId = React.useRef(Math.random().toString());
  const soundRef = React.useRef<Audio.Sound>();
  const autoPlayRef = React.useRef(autoPlay);
  const soundIntervalTimeoutRef = React.useRef<NodeJS.Timeout>();

  const [isLoading, setIsLoading] = React.useState(false);
  const [status, setStatus] = React.useState<AVPlaybackStatusSuccess>();
  const [error, setError] = React.useState<AVPlaybackStatusError>();

  const [currentSoundId, setCurrentSoundId] = useAtom(soundAtom);

  const isPlaying = status?.isPlaying;

  const handleSetStatus = React.useCallback((status: AVPlaybackStatus) => {
    if (status.isLoaded) {
      setStatus(status);
    } else {
      setError(status);
    }
  }, []);

  const playSound = React.useCallback(
    async (fromMs?: number, toMs?: number) => {
      const status = await soundRef.current?.getStatusAsync().catch(() => null);

      if (!status?.isLoaded || status.isPlaying) {
        return;
      }

      if (fromMs !== undefined && toMs !== undefined) {
        const timeOutDelay = toMs - fromMs - PLAY_SOUND_DELAY_MS;
        const timeOutDelayMs = Math.max(50, timeOutDelay);

        soundIntervalTimeoutRef.current = setTimeout(() => {
          soundRef.current?.pauseAsync();
        }, timeOutDelayMs);
      }

      if (
        audioBehaviorMode === 'STOP_OTHERS_AND_BE_STOPPED' ||
        audioBehaviorMode === 'STOP_OTHERS_ONLY'
      ) {
        setCurrentSoundId(soundId.current);
      }

      if (fromMs !== undefined) {
        const fromTimeMs = Math.max(0, fromMs - PLAY_SOUND_DELAY_MS);
        return soundRef.current?.playFromPositionAsync(fromTimeMs);
      } else {
        return soundRef.current?.playAsync();
      }
    },
    [audioBehaviorMode, setCurrentSoundId]
  );

  React.useEffect(() => {
    async function loadSound() {
      if (!audioUrl) {
        return;
      }

      setIsLoading(true);
      try {
        let uri = audioUrl;

        const isBase64 = audioUrl.startsWith('data:audio');
        if (Platform.OS !== 'web' && isBase64 && saveBase64AudioToCache) {
          const soundName = soundId.current.substring(2);
          uri = await saveAudioToLocalCache(audioUrl, soundName, cacheFolder);
        }

        const { sound, status } = await Audio.Sound.createAsync({ uri });

        handleSetStatus(status);
        await sound.setProgressUpdateIntervalAsync(updateIntervalMs);
        sound.setOnPlaybackStatusUpdate(handleSetStatus);
        soundRef.current = sound;

        if (autoPlayRef.current) {
          autoPlayRef.current = false;
          playSound();
        }
      } catch {
        setError({
          isLoaded: false,
          error: 'Error loading sound',
        });
      }
      setIsLoading(false);
    }

    loadSound();

    return () => {
      clearTimeout(soundIntervalTimeoutRef.current);

      soundRef.current?.setOnPlaybackStatusUpdate(null);
      soundRef.current?.stopAsync().catch(() => {});
      soundRef.current?.unloadAsync().catch(() => {});
    };
  }, [
    audioUrl,
    autoPlay,
    updateIntervalMs,
    cacheFolder,
    saveBase64AudioToCache,
    handleSetStatus,
    playSound,
  ]);

  const pauseSound = React.useCallback(async () => {
    const status = await soundRef.current?.getStatusAsync().catch(() => null);

    if (!status?.isLoaded || !status.isPlaying) {
      return;
    }

    return soundRef.current?.pauseAsync();
  }, []);

  const stopSound = React.useCallback(async () => {
    const status = await soundRef.current?.getStatusAsync().catch(() => null);
    if (!status || !status?.isLoaded) {
      return;
    }

    return soundRef.current?.stopAsync();
  }, []);

  const toggleStartStopSound = React.useCallback(async () => {
    const status = await soundRef.current?.getStatusAsync().catch(() => null);
    if (!status || !status?.isLoaded) {
      return;
    }

    if (status.isPlaying) {
      return stopSound();
    } else {
      await soundRef.current?.stopAsync();
      return playSound();
    }
  }, [stopSound, playSound]);

  const setSoundPosition = React.useCallback(async (position: number) => {
    const status = await soundRef.current?.getStatusAsync().catch(() => null);
    if (!status || !status?.isLoaded) {
      return;
    }

    return soundRef.current?.setPositionAsync(position);
  }, []);

  const setSoundRate = React.useCallback(
    async (
      rate: number,
      shouldCorrectPitch: boolean,
      pitchCorrectionQuality?: Audio.PitchCorrectionQuality | undefined
    ) => {
      const status = await soundRef.current?.getStatusAsync().catch(() => null);
      if (!status || !status?.isLoaded) {
        return;
      }

      return soundRef.current?.setRateAsync(
        rate,
        shouldCorrectPitch,
        pitchCorrectionQuality
      );
    },
    []
  );

  const getSoundStatus = React.useCallback(async () => {
    const status = await soundRef.current?.getStatusAsync().catch(() => null);
    if (!status || !status?.isLoaded) {
      return;
    }

    return status;
  }, []);

  React.useEffect(() => {
    const shouldStopAudio =
      audioBehaviorMode === 'STOP_OTHERS_AND_BE_STOPPED' ||
      audioBehaviorMode === 'BE_STOPPED_ONLY';

    if (isPlaying && shouldStopAudio && currentSoundId !== soundId.current) {
      pauseSound();
    }
  }, [currentSoundId, isPlaying, audioBehaviorMode, pauseSound]);

  return {
    error,
    status,
    isLoading,
    isPlaying,
    playSound,
    pauseSound,
    stopSound,
    setSoundPosition,
    setSoundRate,
    getSoundStatus,
    toggleStartStopSound,
  };
}
